import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '@/shared/ui';
import { StationSelector } from '../../../../components/StationSelector';
import type { StationChainFormApi } from '../useStationChainForm';
import type { StationChainEditor } from '../useStationChainEditor';
import { NewStationFromMapForm } from './NewStationFromMapForm';
import { StopRow } from './StopRow';
import { TravelStrip } from './TravelStrip';
import { formatDuration, totalDistanceKm, totalDurationMin } from './chainStopUtils';

export interface StopsPanelProps {
  form: StationChainFormApi;
  editor: StationChainEditor;
}

/** Panel se zastávkami trasy — pořadí, přejezdy, stání; přidávání pod seznamem; souhrn v hlavičce/patičce. */
export function StopsPanel({ form, editor }: StopsPanelProps) {
  const { t } = useTranslation('products');
  const {
    dragIndex,
    dragOverIndex,
    setDragIndex,
    setDragOverIndex,
    reorder,
    removeStop,
    setDwell,
    setTravelManual,
    resetTravelAuto,
    setStopDetail,
    appendStations,
    newStationCoord,
    handleStationCreated,
    cancelNewStation,
  } = editor;

  return (
    <Panel
      variant="filled"
      icon="pin"
      title={t('stationChain.stops.panelTitle')}
      badge={
        <form.Subscribe selector={(state) => state.values.stops}>
          {(stops) =>
            stops.length > 0 ? (
              <span className="text-phx-sm text-phx-text-muted">
                {t('stationChain.stops.headerSummary', {
                  count: stops.length,
                  distance: totalDistanceKm(stops),
                })}
              </span>
            ) : null
          }
        </form.Subscribe>
      }
      footer={
        <form.Subscribe selector={(state) => state.values.stops}>
          {(stops) => (
            <>
              <span className="text-phx-text-muted text-phx-xs">
                {t('stationChain.stops.footerStops', { count: stops.length })}
              </span>
              <span className="flex-1" />
              <span className="text-phx-text-muted text-phx-xs">
                {t('stationChain.stops.footerDuration')}{' '}
                <strong className="text-phx-text">{formatDuration(totalDurationMin(stops))}</strong>
              </span>
            </>
          )}
        </form.Subscribe>
      }
    >
      {newStationCoord ? (
        <div className="mb-phx-3">
          <NewStationFromMapForm
            lat={newStationCoord.lat}
            lng={newStationCoord.lng}
            onCreated={handleStationCreated}
            onCancel={cancelNewStation}
          />
        </div>
      ) : null}

      <form.Subscribe selector={(state) => ({ stops: state.values.stops, color: state.values.color })}>
        {({ stops, color }) =>
          stops.length === 0 ? (
            <p className="text-phx-text-muted text-phx-sm p-phx-2">{t('stationChain.stops.empty')}</p>
          ) : (
            <div className="flex flex-col">
              {stops.map((stop, index) => (
                <Fragment key={stop.key}>
                  {index > 0 ? (
                    <TravelStrip
                      travel={stop.travel}
                      travelAuto={stop.travelAuto}
                      onChange={(raw) => setTravelManual(index, raw)}
                      onReset={() => resetTravelAuto(index)}
                    />
                  ) : null}
                  <DropIndicator
                    visible={dragIndex !== null && dragOverIndex === index && dragIndex !== index}
                  />
                  <StopRow
                    stop={stop}
                    index={index}
                    color={color}
                    dragging={dragIndex === index}
                    onDragStart={() => setDragIndex(index)}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                    onDragOver={(event) => {
                      if (dragIndex !== null) {
                        event.preventDefault();
                        setDragOverIndex(index);
                      }
                    }}
                    onDrop={() => {
                      if (dragIndex !== null) reorder(dragIndex, index);
                      setDragIndex(null);
                      setDragOverIndex(null);
                    }}
                    onRemove={() => removeStop(index)}
                    onDwell={(raw) => setDwell(index, raw)}
                    onSetDetail={(id) => setStopDetail(index, id)}
                  />
                </Fragment>
              ))}
              {dragIndex !== null ? (
                <div
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragOverIndex(stops.length);
                  }}
                  onDrop={() => {
                    reorder(dragIndex, stops.length);
                    setDragIndex(null);
                    setDragOverIndex(null);
                  }}
                  className="py-phx-1"
                >
                  <DropIndicator visible={dragOverIndex === stops.length} />
                </div>
              ) : null}
            </div>
          )
        }
      </form.Subscribe>

      {/* Přidání stanice — tlačítko přes celou šířku pod seznamem (dle návrhu). Button mód selectoru
          už nese ikonu „+"; vlastní field label skryjeme (`.phx-label`), trigger roztáhneme na 100 %. */}
      <div className="pt-phx-3 [&_.phx-label]:hidden [&_.phxsel]:w-full [&_.phxsel-trigger]:w-full">
        <StationSelector
          mode="multi"
          trigger="button"
          addMode
          label={t('stationChain.stops.add')}
          buttonLabel={t('stationChain.stops.add')}
          buttonVariant="outline"
          confirmLabel={t('stationChain.stops.addConfirm')}
          selected={[]}
          onChange={appendStations}
        />
      </div>
    </Panel>
  );
}

/** Vizualizace místa dropu při drag & drop přeřazování zastávek. */
function DropIndicator({ visible }: { visible: boolean }) {
  if (!visible) {
    return null;
  }
  return (
    <div aria-hidden className="bg-phx-primary my-px h-0.5 rounded-full" data-testid="drop-indicator" />
  );
}
